import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { QaInspection, QaStatus, QaResult } from '../types/maintenance-qa.types';
import { useQa } from '../hooks/useQa';
import { toast } from 'sonner';

interface QaFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: QaInspection | null;
  companyId: string;
}

const statusOptions: { value: QaStatus; label: string }[] = [
  { value: 'draft', label: 'Bản nháp' },
  { value: 'pending_inspection', label: 'Chờ kiểm tra' },
  { value: 'inspecting', label: 'Đang kiểm tra' },
];

export function QaFormModal({ isOpen, onClose, item, companyId }: QaFormModalProps) {
  const isEdit = !!item;
  const { createInspection, isCreating } = useQa(companyId);

  const [formData, setFormData] = useState<Partial<QaInspection>>({
    code: '',
    status_code: 'draft',
    project_id: '',
    work_order_id: '',
    checklist_id: '',
    inspector_id: '',
    notes: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData(item);
      } else {
        setFormData({
          code: `QA-${Date.now().toString().slice(-6).toUpperCase()}`,
          status_code: 'draft',
          project_id: '',
          work_order_id: '',
          checklist_id: '',
          inspector_id: '',
          notes: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!isEdit) {
        await createInspection({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật đợt kiểm tra thành công' : 'Tạo đợt kiểm tra QA mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Cập nhật đợt kiểm tra QA' : 'Thiết lập đợt kiểm tra QA mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã đợt kiểm tra (Tự động)"
            required
            value={formData.code || ''}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
          />
          <Input
            label="ID Dự án *"
            required
            value={formData.project_id || ''}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ID Lệnh công việc (WO) *"
            required
            value={formData.work_order_id || ''}
            onChange={(e) => setFormData({ ...formData, work_order_id: e.target.value })}
          />
          <Input
            label="ID Thanh tra (Inspector) *"
            required
            value={formData.inspector_id || ''}
            onChange={(e) => setFormData({ ...formData, inspector_id: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ID Bộ tiêu chuẩn (Checklist)"
            value={formData.checklist_id || ''}
            onChange={(e) => setFormData({ ...formData, checklist_id: e.target.value })}
          />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái ban đầu *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || ''}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as QaStatus })}
            >
              {statusOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Lưu ý cho đợt kiểm tra</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
            placeholder="Các tiêu chuẩn kỹ thuật cần chú ý hoặc bối cảnh kiểm tra..."
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isCreating}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isCreating}>
            {isEdit ? 'Lưu thay đổi' : 'Thiết lập kiểm tra'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
